import { useState } from "react";
import {
  Check,
  Copy,
  Link2,
  Loader2,
  Mail,
  MessageCircle,
  PowerOff,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { api, errorMessage } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { formatDate } from "@/lib/format";

const ACTION =
  "inline-flex items-center justify-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50 disabled:opacity-60";

export const PortalLinkSheet = ({ scope = "contact", id, name }) => {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);

  const base =
    scope === "firm"
      ? `/firms/${id}/portal`
      : scope === "client"
        ? `/clients/${id}/portal`
        : `/contacts/${id}/portal`;
  const noun = scope === "firm" ? "firm" : scope === "client" ? "client" : "contact";

  const run = async (key, fn) => {
    setBusy(key);
    try {
      return await fn();
    } catch (error) {
      toast.error(errorMessage(error, "Could not update the account page"));
      return null;
    } finally {
      setBusy("");
    }
  };

  const openSheet = async () => {
    setOpen(true);
    setLink(null);
    await run("load", async () => {
      const { data } = await api.get(base);
      setLink(data);
    });
  };

  const enable = () =>
    run("enable", async () => {
      const { data } = await api.post(base);
      setLink(data);
      toast.success("Account page is live");
    });

  const rotate = () =>
    run("rotate", async () => {
      if (!window.confirm("Issue a new link? The old one stops working immediately.")) return;
      const { data } = await api.post(`${base}/rotate`);
      setLink(data);
      toast.success("New link issued");
    });

  const disable = () =>
    run("disable", async () => {
      const { data } = await api.delete(base);
      setLink(data);
      toast.success("Account page switched off");
    });

  const copy = async () => {
    await navigator.clipboard.writeText(link.url);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const sendEmail = () =>
    run("email", async () => {
      const { data } = await api.post(`${base}/send-email`);
      toast.success(`Link emailed to ${data.recipient}`);
      const fresh = await api.get(base);
      setLink(fresh.data);
    });

  const sendWhatsApp = () =>
    run("whatsapp", async () => {
      const { data } = await api.post(`${base}/whatsapp`);
      openWhatsApp(data);
    });

  return (
    <>
      <button
        type="button"
        onClick={openSheet}
        title="Client account page"
        data-testid={`portal-link-${id}`}
        className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-slate-400 hover:text-slate-900"
      >
        <Link2 className="h-3.5 w-3.5" strokeWidth={1.6} />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="font-serif text-xl">
              {scope === "firm" ? "Firm account page" : "Client account page"}
            </SheetTitle>
            <SheetDescription className="text-[12px]">
              A private, always-current page for {name} — live outstanding balance, every fee note
              and every receipt as a PDF
              {scope === "firm" ? ", sub-grouped by the lawyer who briefed the work" : ""}. No
              password, so share the link only with them.
            </SheetDescription>
          </SheetHeader>

          {busy === "load" || !link ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : !link.enabled ? (
            <div className="mt-7 space-y-4" data-testid="portal-off-state">
              <p className="text-[13px] text-slate-600">
                No account page is live for this {scope === "firm" ? "firm" : "contact"} yet.
              </p>
              <button
                type="button"
                onClick={enable}
                disabled={busy === "enable"}
                data-testid="portal-enable-button"
                className="inline-flex w-full items-center justify-center gap-2 bg-slate-900 px-5 py-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
              >
                {busy === "enable" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Link2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                )}
                Create the account page
              </button>
            </div>
          ) : (
            <div className="mt-7 space-y-5" data-testid="portal-on-state">
              <div className="border border-slate-200 bg-slate-50 p-4">
                <p className="label-caps">Secure link</p>
                <p
                  className="num mt-2 break-all text-[12px] text-slate-800"
                  data-testid="portal-url"
                >
                  {link.url}
                </p>
                <p className="mt-3 text-[11px] text-slate-500">
                  Opened {link.view_count} time(s)
                  {link.last_viewed_at
                    ? ` · last on ${formatDate(link.last_viewed_at.slice(0, 10))}`
                    : " · not opened yet"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={copy} data-testid="portal-copy-button" className={ACTION}>
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.8} />
                  ) : (
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.8} />
                  )}
                  Copy link
                </button>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="portal-open-button"
                  className={ACTION}
                >
                  <Link2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                  Open page
                </a>
                <button
                  type="button"
                  onClick={sendEmail}
                  disabled={busy === "email"}
                  data-testid="portal-email-button"
                  className="inline-flex items-center justify-center gap-2 bg-slate-900 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
                >
                  {busy === "email" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Mail className="h-3.5 w-3.5" strokeWidth={1.8} />
                  )}
                  Email link
                </button>
                <button
                  type="button"
                  onClick={sendWhatsApp}
                  disabled={busy === "whatsapp"}
                  data-testid="portal-whatsapp-button"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-700 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-emerald-800 disabled:opacity-60"
                >
                  {busy === "whatsapp" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.8} />
                  )}
                  WhatsApp
                </button>
              </div>

              <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-5">
                <button
                  type="button"
                  onClick={rotate}
                  disabled={busy === "rotate"}
                  data-testid="portal-rotate-button"
                  className={ACTION}
                >
                  <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.8} />
                  Issue a new link
                </button>
                <button
                  type="button"
                  onClick={disable}
                  disabled={busy === "disable"}
                  data-testid="portal-disable-button"
                  className="inline-flex items-center justify-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-red-600 transition-colors duration-150 hover:border-red-300 hover:bg-red-50 disabled:opacity-60"
                >
                  <PowerOff className="h-3.5 w-3.5" strokeWidth={1.8} />
                  Switch off
                </button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
